import { Client, IMessage } from '@stomp/stompjs';

import { formatTopicId, formatTopicPath } from '@webtrader/core/format/socket';
import storage, { EStorageKey } from '@webtrader/core/storage';

export enum EChartTopicPeriod {
  MINUTE_1 = '1m',
  MINUTE_2 = '2m',
  MINUTE_3 = '3m',
  MINUTE_4 = '4m',
  MINUTE_5 = '5m',
  MINUTE_6 = '6m',
  MINUTE_10 = '10m',
  MINUTE_15 = '15m',
  MINUTE_20 = '20m',
  MINUTE_30 = '30m',
  HOUR_1 = '1h',
  HOUR_2 = '2h',
  HOUR_3 = '3h',
  HOUR_4 = '4h',
  HOUR_6 = '6h',
  DAY = '1d',
  WEEK = '1w',
  MONTH = '1mth'
}

export enum SYMBOL_MESSAGE_TYPE {
  LIVE = 'LIVE'
}

export enum CHART_MESSAGE_TYPE {
  MOVE = 'MOVE',
  PATCH = 'PATCH'
}

export interface IChartTopicPatchData {
  T: number; // time
  C: number; // close
  H: number; // high
  L: number; // low
  O: number; // open
  A: number; // ask
}

export interface IChartTopicMessage {
  M: CHART_MESSAGE_TYPE;
  D: IChartTopicPatchData;
}

export interface IChartTopicReceiver {
  (payload: IChartTopicMessage | undefined): void;
}

export interface ISubscribeParams {
  symbol: string;
  period: EChartTopicPeriod;
  receiver: IChartTopicReceiver;
}

interface IProps {
  client: Client;
}

class ChartTopic {
  private client: Client;
  private receivers: Record<string, Set<IChartTopicReceiver> | undefined> = {};

  constructor(props: IProps) {
    const { client } = props;

    this.client = client;
  }

  public subscribe = (params: ISubscribeParams) => {
    const { period, symbol, receiver } = params;

    const { graphId, headerId } = this.mapPeriodToIds(period, symbol);

    if (this.receivers[graphId]) {
      this.receivers[graphId]?.add(receiver);
    } else {
      this.receivers[graphId] = new Set([receiver]);

      this.client.subscribe(formatTopicPath(graphId), (message) => this.onReceive(graphId, message), {
        id: headerId
      });
    }
  };

  public unsubscribe = (params: ISubscribeParams) => {
    const { period, symbol, receiver } = params;

    const { graphId, headerId } = this.mapPeriodToIds(period, symbol);

    if (!this.client) {
      return;
    }

    this.receivers[graphId]?.delete(receiver);

    if (this.receivers[graphId]?.size === 0) {
      this.client.unsubscribe(headerId);

      this.receivers[graphId] = undefined;
    }
  };

  public reconnect = () => {
    const { receivers } = this;

    if (receivers) {
      Object.keys(receivers).forEach((subPath) => {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: formatTopicId(subPath)
        });
      });
    }
  };

  private onReceive = (id: string, message: IMessage) => {
    const receivers = this.receivers[id];

    if (!receivers || !message) {
      return;
    }

    const payload = JSON.parse(message.body);

    receivers.forEach((receiver) => receiver(payload));
  };

  private mapPeriodToIds = (period: EChartTopicPeriod, symbol: string) => {
    const marketSubscription = storage.getItem<string>(EStorageKey.MARKET_SUBSCRIPTION);
    const subPath = `chart${period}/${marketSubscription}/${symbol}`;

    return {
      graphId: subPath,
      headerId: formatTopicId(subPath)
    };
  };
}

export default ChartTopic;
